import {graphql} from 'gatsby'
import React from 'react'

import FeatureBlock from '@/components/feature-block'
import {ProofPointBadges} from '@/components/proof-point-badges/proof-point-badges'
import SimpleSignupForm from '@/components/simple-signup-form'
import type {HeroSliceFragment, SignupBannerFragment} from '@/graphql-types'
import {Section} from '@/styles/grid'
import type {FormConfig} from '@/types/consents'
import {LeadInterestLevel} from '@/types/marketing-campaigns'
import getEnvironmentFormConfig from '@/utils/get-environment-field'
import {DocTypesEnum} from '@/utils/link-resolver'

enum ComponentName {
    SignupBanner = 'signup_banner',
}

export const HeroSlice = ({slice, dataTestId}: {slice: HeroSliceFragment; dataTestId?: string}) => {
    const predefinedSignupBanner = slice.primary.predefined_signup_banner
    const video =
        slice.primary.video_webm?.url || slice.primary.video_mp4?.url
            ? {
                  webm: slice.primary.video_webm?.url,
                  mp4: slice.primary.video_mp4?.url,
              }
            : undefined
    const isOversizedImage = !!slice.primary.is_oversized_image
    const formType = slice.primary.predefined_signup_banner.type
    const formContent = predefinedSignupBanner.document as SignupBannerFragment
    const redirectsToParterBigformPage =
        formContent?.data.redirect_link?.type === DocTypesEnum.PARTNER_BIGFORM_PAGE

    if (formType && formType !== ComponentName.SignupBanner) {
        throw new Error('Unrecognized Form type')
    }

    return (
        <Section
            id={`pleo-page-section-${slice.primary?.section_html_id}`}
            padding="small"
            hideOverflow={isOversizedImage}
            data-testid={dataTestId}
        >
            <FeatureBlock
                videoId={slice.primary.wistia_video_id}
                inlineVideo={video}
                image={{...slice.primary.image, loading: 'eager'}}
                title={slice.primary.title}
                body={slice.primary.lead_text.richText}
                mirrored={!!slice.primary.inverse}
                oversizedImage={isOversizedImage}
                titleVariant="heading-base"
                bodyProps={{$variant: 'paragraph-large'}}
                headingLevel="h1"
            >
                {formContent && (
                    <SimpleSignupForm
                        ariaLabel={formContent.data.aria_label}
                        placeholder={formContent.data.email_input_placeholder}
                        submitText={formContent.data.submit_text}
                        errorText={formContent.data.email_input_error_text}
                        signupOptions={{
                            form: formContent.data.form_name,
                            isPartner: formContent.data.partner_signup_redirect,
                            redirectPath:
                                formContent.data.redirect_link?.uid &&
                                `/${formContent.data.redirect_link.uid}`,
                            leadInterestLevel: redirectsToParterBigformPage
                                ? LeadInterestLevel.Interested
                                : undefined,
                        }}
                        dataTestId={dataTestId}
                        formConfig={
                            getEnvironmentFormConfig({
                                production: formContent.data.form_production,
                                staging: formContent.data.form_staging,
                            }) as FormConfig
                        }
                        withBorder
                    />
                )}

                {!!slice.primary.predefined_proof_point_badges?.document && (
                    <ProofPointBadges
                        proofPoints={[
                            slice.primary.predefined_proof_point_badges.document.data
                                .proof_point_first[0],
                            slice.primary.predefined_proof_point_badges.document.data
                                .proof_point_second[0],
                        ]}
                    />
                )}
            </FeatureBlock>
        </Section>
    )
}

export const heroSliceFragment = graphql`
    fragment HeroSlice on PrismicSlicePageDataBodyHero {
        __typename
        primary {
            section_html_id
            title
            inverse
            is_oversized_image
            lead_text {
                richText
            }
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            wistia_video_id
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
            predefined_signup_banner {
                type
                document {
                    ...SignupBanner
                }
            }
            predefined_proof_point_badges {
                document {
                    ... on PrismicProofPointBadgesRepeatable {
                        data {
                            proof_point_first {
                                title
                                subtitle
                                icon {
                                    alt
                                    copyright
                                    url
                                    dimensions {
                                        height
                                        width
                                    }
                                }
                            }
                            proof_point_second {
                                title
                                subtitle
                                icon {
                                    alt
                                    copyright
                                    url
                                    dimensions {
                                        height
                                        width
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`
